/**
 * Колесо фортуни — один безкоштовний оберт на місяць (owner 2026-09-01).
 *
 * Поки що ЛИШЕ вигляд і відчуття: сервер нічого не знає про оберти, приз
 * обирається на клієнті. Спершу домовляємось про сторінку, потім робимо
 * механіку — owner: «зайвого поки що не пиши з точки зору коду».
 *
 * Два рішення, які тут навмисно саме такі:
 * 1. Підпис призу читається ВЗДОВЖ сектора в один рядок (owner 2026-09-01).
 *    Ліва половина колеса розвернута на 180°, інакше там текст догори ногами.
 * 2. Сторінка не малює власних горизонтальних відступів — їх дає Layout
 *    (WIDTH CONTRACT), тому краї збігаються з рештою розділів.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  useRefreshWheel,
  useWheelPurchase,
  useWheelSpin,
  useWheelState,
  type WheelSpinOut,
  type WheelStateOut,
} from "@/api/hooks";
import { BackNav } from "@/components/BackNav";
import { markWheelPurchasePending, purchasePopupPending } from "@/components/WheelPurchaseWatcher";
import { reportDiag } from "@/lib/clientDiag";
import { spinsLabel } from "@/lib/spinsWord";
import { PageHeader } from "@/components/PageHeader";
import { Icon } from "@/components/Icon";
import { Section } from "@/components/Section";
import { WheelNoSpinPopup } from "@/components/WheelNoSpinPopup";
import { WheelPacksPopup } from "@/components/WheelPacksPopup";
import { WheelPrizePopup } from "@/components/WheelPrizePopup";
import { WheelDoublePopup } from "@/components/WheelDoublePopup";
import { WheelDoubleSkipPopup } from "@/components/WheelDoubleSkipPopup";
import { markWheelDouble, prefetchWheelDouble, useWheelDouble } from "@/api/hooks";
import { tg } from "@/lib/telegram";
import { openTelegramUserLink } from "@/lib/openExternal";
import { showAppAlert } from "@/lib/appAlert";
import { ukrDaysWord } from "@/lib/utils";

interface Prize {
  /** Велика частина підпису в секторі. */
  value: string;
  /** Друга частина того ж рядка капітеллю. */
  unit: string;
  /** Перший рядок у секторі. */
  l1: string;
  /** Другий рядок у секторі. Розбиваємо так, щоб рядки були приблизно
   *  однакової довжини (owner 2026-09-02: «7 днів» / «підписки», а не
   *  «7» / «днів підписки»). */
  l2: string;
  /** Короткий рядок під призом у попапі. */
  desc: string;
  emoji: string;
  /** Джекпот — пофарбований сектор. */
  best?: boolean;
  /** Другий за вагою — підсвічений, але спокійніше. */
  good?: boolean;
  /** Що це за приз: від нього залежить кнопка в попапі. */
  kind: "bonus" | "days" | "promo" | "spin";
}


/** 16 секторів. Порядок у колесі — саме такий, як у масиві і на сервері
 *  (`services/wheel.py::PRIZES`, драбина з дрібними призами — owner 2026-09-25). */
const PRIZES: Prize[] = [
  { value: "20", unit: "бонусів", l1: "20", l2: "бонусів",
    desc: "Використай як знижку на наступну підписку", emoji: "💰", kind: "bonus" },
  { value: "3", unit: "дні підписки", l1: "3 дні", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", kind: "days" },
  { value: "50", unit: "бонусів", l1: "50", l2: "бонусів",
    desc: "Використай як знижку на наступну підписку", emoji: "💰", kind: "bonus" },
  { value: "2", unit: "місяці підписки", l1: "2 місяці", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", good: true, kind: "days" },
  { value: "1", unit: "день підписки", l1: "1 день", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", kind: "days" },
  { value: "Ще", unit: "оберт", l1: "Ще один", l2: "оберт",
    desc: "Крути колесо ще раз, прямо зараз", emoji: "⚡", kind: "spin" },
  { value: "7", unit: "днів підписки", l1: "7 днів", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", kind: "days" },
  { value: "6", unit: "місяців підписки", l1: "6 місяців", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", good: true, kind: "days" },
  { value: "20", unit: "бонусів", l1: "20", l2: "бонусів",
    desc: "Використай як знижку на наступну підписку", emoji: "💰", kind: "bonus" },
  { value: "2", unit: "дні підписки", l1: "2 дні", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", kind: "days" },
  { value: "10", unit: "днів підписки", l1: "10 днів", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", kind: "days" },
  { value: "Рік", unit: "підписки", l1: "Рік", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", best: true, kind: "days" },
  { value: "100", unit: "бонусів", l1: "100", l2: "бонусів",
    desc: "Використай як знижку на наступну підписку", emoji: "💰", kind: "bonus" },
  { value: "5", unit: "днів підписки", l1: "5 днів", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", kind: "days" },
  { value: "Місяць", unit: "підписки", l1: "Місяць", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", kind: "days" },
  { value: "3", unit: "місяці підписки", l1: "3 місяці", l2: "підписки",
    desc: "Підписка активується одразу — більше нічого робити не треба", emoji: "⏳", good: true, kind: "days" },
];

/** Ключі призів у ТОМУ САМОМУ порядку, що й сектори на сервері
 *  (`services/wheel.py::PRIZES`). Потрібні, щоб за ключем показати
 *  правильний попап людині, яка приходить за кнопкою «Забрати приз». */
const SERVER_PRIZE_KEYS = [
  "bonus_20_a",
  "days_3",
  "bonus_50",
  "days_60",
  "days_1",
  "spin_extra",
  "days_7",
  "days_180",
  "bonus_20_b",
  "days_2",
  "days_10",
  "days_365",
  "bonus_100",
  "days_5",
  "month_free",
  "days_90",
];

/** Приз із нашої таблиці за ключем сервера; невідомий ключ — null. */
function prizeByKey(key: string | null | undefined): Prize | null {
  const idx = key ? SERVER_PRIZE_KEYS.indexOf(key) : -1;
  return idx >= 0 ? PRIZES[idx] : null;
}

/** Приз, якого людина не побачила (05.10.2026: колесо крутилось без
 *  зупинки, людина закрила застосунок — приз нараховано, а попапа не було).
 *  Такий приз показуємо при вході в колесо, поки йому не більше тижня. */
const UNSEEN_PRIZE_MS = 7 * 24 * 60 * 60 * 1000;
/** Час оберту, приз якого людина вже бачила. Один рядок, без накопичення. */
const PRIZE_SEEN_KEY = "wheel_prize_seen";
/** Запасна памʼять, якщо localStorage недоступний: тоді попап хоча б не
 *  спливе вдруге в межах цього запуску застосунку. */
const prizeSeenInSession = new Set<string>();

function prizeSeen(spunAt: string): boolean {
  if (prizeSeenInSession.has(spunAt)) return true;
  try {
    return localStorage.getItem(PRIZE_SEEN_KEY) === spunAt;
  } catch {
    return false;
  }
}

function markPrizeSeen(spunAt: string | null | undefined): void {
  if (!spunAt) return;
  prizeSeenInSession.add(spunAt);
  try {
    localStorage.setItem(PRIZE_SEEN_KEY, spunAt);
  } catch {
    /* приватний режим — лишається памʼять сесії */
  }
}

function isFreshSpin(spunAt: string, now: number = Date.now()): boolean {
  const t = Date.parse(spunAt);
  return Number.isFinite(t) && now - t <= UNSEEN_PRIZE_MS;
}

/** Назва призу в попапі — як на секторі, двома рядками в один: «Ще один
 *  оберт», «3 дні підписки», «Рік підписки» (owner 2026-09-23: «оберт»
 *  маленькими літерами виглядав недописаним). */
function prizeTitle(p: Prize): string {
  const t = `${p.l1} ${p.l2}`.trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Місяць і більше — це вже джекпот, його святкуємо окремо. */
function isJackpot(p: Prize): boolean {
  return p.kind === "days" && (p.best === true || p.good === true || p.l1 === "Місяць");
}

/** Приз у «Топі перемог місяця» — коротким підписом. */
const WINNER_LABEL: Record<string, string> = {
  bonus_20_a: "20 бонусів",
  bonus_20_b: "20 бонусів",
  bonus_50: "50 бонусів",
  days_1: "1 день",
  days_2: "2 дні",
  days_3: "3 дні",
  bonus_50_a: "50 бонусів",
  bonus_50_b: "50 бонусів",
  bonus_100_a: "100 бонусів",
  bonus_100_b: "100 бонусів",
  bonus_150: "150 бонусів",
  bonus_200: "200 бонусів",
  days_3_a: "3 дні",
  days_3_b: "3 дні",
  days_7: "7 днів",
  month_free: "Місяць",
  spin_extra: "Ще оберт",
  bonus_100: "100 бонусів",
  bonus_300: "300 бонусів",
  days_5: "5 днів",
  days_10: "10 днів",
  days_14: "14 днів",
  days_60: "2 місяці",
  days_90: "3 місяці",
  days_180: "6 місяців",
  days_365: "Рік",
  // Призи, яких на колесі вже немає, — у топі місяця ще трапляються.
  promo_2m: "1+1",
};

/** Великий виграш підсвічуємо в топі місяця: місяць і більше. */
function isBigWin(key: string | null | undefined): boolean {
  // Рядок без призу не має валити всю сторінку (білий екран на стенді).
  if (!key) return false;
  if (key === "month_free" || key === "promo_2m") return true;
  const days = key.startsWith("days_") ? Number(key.slice(5)) : 0;
  return Number.isFinite(days) && days >= 30;
}

/** Скільки минуло: «4 хв тому», «2 год тому», «вчора», «3 дні тому».
 *  Дні — повним словом (owner 2026-09-01: фраз не обрізаємо): у топі місяця
 *  більшість рядків старші за добу, і «дн.» стояло б майже в кожному. */
function agoLabel(iso: string | null, now: number): string {
  if (!iso) return "";
  const min = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
  if (min < 1) return "щойно";
  if (min < 60) return `${min} хв тому`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} год тому`;
  const d = Math.floor(h / 24);
  return d === 1 ? "вчора" : `${d} ${ukrDaysWord(d)} тому`;
}


/** Тони для кружечка з першою літерою — він лишається там, де фото з
 *  Telegram немає чи воно не завантажилось. */
const AVATAR_TONES = ["#FE6B04", "#D05703", "#C9A227", "#7A5E12", "#B4552A"];

/** Кружечок людини в «Топі перемог місяця» (owner 2026-10-11): фото профілю
 *  з Telegram, а якщо його немає, людина сховала його від ботів чи воно не
 *  завантажилось — літера, як і раніше.
 *
 *  Фото лежить ПОВЕРХ літери й зʼявляється, лише коли завантажилось: розмір і
 *  місце кружечка ті самі в обох станах, рядок не стрибає, і битої картинки
 *  людина не бачить ніколи. */
function WinnerAvatar({
  name,
  url,
  tone,
}: {
  name: string;
  url?: string | null;
  tone: string;
}) {
  const [photo, setPhoto] = useState<"wait" | "ok" | "bad">("wait");
  return (
    <div
      className="relative grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full font-display text-[0.8125rem] font-bold text-white"
      style={{ background: tone }}
    >
      {name.slice(0, 1)}
      {url && photo !== "bad" && (
        <img
          src={url}
          alt={name}
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => setPhoto("ok")}
          onError={() => setPhoto("bad")}
          className={
            "absolute inset-0 h-full w-full rounded-full object-cover " +
            (photo === "ok" ? "fade-in" : "opacity-0")
          }
        />
      )}
    </div>
  );
}

/** Строге чергування: білий — бежевий (owner 2026-09-02). Два акцентні
 *  сектори («1+1» і «Місяць») стоять на непарних місцях, тому ритм решти
 *  не збивається. */
const SHADES = ["#FFFFFF", "#F5EDE0"];

const N = PRIZES.length;
const STEP = 360 / N;
const C = 260;
const R_OUT = 238;  // сектори майже до обода — порожньої смуги немає
const R_IN = 76;  // ступиця більша — усередині кнопка «Крутити»
/** Носик стрілки — рівно на кульках у кутах секторів. */
/** Підпис стоїть на ОДНОМУ радіусі для всіх секторів і пишеться у два рядки:
 *  значення, під ним — одиниця (owner 2026-09-02). Один радіус на всіх — і
 *  підписи вишикувані по колу, а не «пливуть» залежно від довжини. */
const R_LABEL = 156;
/** Кегль першого рядка підпису залежить від довжини: довгі слова дрібніші. */
const l1Size = (t: string) => (t.length > 5 ? 15 : t.length > 3 ? 16.5 : 19);
const L2_SIZE = 10.5;
const L2_GAP = 16; // від базової лінії першого рядка до базової лінії другого

/** Де поставити підпис, щоб блок із двох рядків стояв ПО ЦЕНТРУ клина.
 *
 *  Owner 2026-09-23: «відступи зверху і знизу різні, рядки наче навскіс».
 *  Причина: базова лінія першого рядка стояла на y=-4 навмання, а в
 *  Unbounded цифри й літери різної висоти — блок зʼїжджав до одного краю
 *  сектора, і в клині це читалось як перекіс. Тепер центруємо за ФАРБОЮ:
 *  висоту й ширину літер міряємо канвасом тими самими шрифтами, що й у SVG.
 *  Повертає зсув базової лінії (y) і поправку по радіусу (dx). */
function measureLabel(l1: string, l2: string): { y: number; dx: number } {
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return { y: -4, dx: 0 };
  ctx.textAlign = "center";
  ctx.font = `800 ${l1Size(l1)}px Unbounded`;
  const m1 = ctx.measureText(l1);
  ctx.font = `700 ${L2_SIZE}px Manrope`;
  const m2 = ctx.measureText(l2.toUpperCase());
  // По дотичній: верх фарби першого рядка і низ фарби другого — рівно
  // навпроти від осі сектора.
  const y = (m1.actualBoundingBoxAscent - L2_GAP - m2.actualBoundingBoxDescent) / 2;
  // По радіусу: центр фарби обох рядків разом — на R_LABEL.
  const left = Math.max(m1.actualBoundingBoxLeft, m2.actualBoundingBoxLeft);
  const right = Math.max(m1.actualBoundingBoxRight, m2.actualBoundingBoxRight);
  const dx = (left - right) / 2;
  return { y: Number.isFinite(y) ? y : -4, dx: Number.isFinite(dx) ? dx : 0 };
}
const PIN_TIP_Y = C - R_OUT + 8;
/** Вісь стрілки — вище диска, тому вона читається окремою деталлю. */
const PIN_PIVOT_Y = C - R_OUT - 26;

/** Кут диска, при якому стрілка стоїть на секторі (трохи за його центром,
 *  як після дотяжки). */
function restAngle(sector: number): number {
  return (((-(sector * STEP + STEP / 2) - 4) % 360) + 360) % 360;
}

function pol(r: number, deg: number): [number, number] {
  const a = (deg * Math.PI) / 180;
  return [C + r * Math.cos(a), C + r * Math.sin(a)];
}

/** Дуга вздовж зовнішнього краю сектора — тонкий акцент для головних призів. */
function arcPath(r: number, i: number, pad = 1.4): string {
  const [x0, y0] = pol(r, -90 + i * STEP + pad);
  const [x1, y1] = pol(r, -90 + (i + 1) * STEP - pad);
  return `M${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 0 1 ${x1.toFixed(2)},${y1.toFixed(2)}`;
}

function sectorPath(i: number): string {
  const [x0, y0] = pol(R_OUT, -90 + i * STEP);
  const [x1, y1] = pol(R_OUT, -90 + (i + 1) * STEP);
  return `M${C},${C} L${x0.toFixed(2)},${y0.toFixed(2)} A${R_OUT},${R_OUT} 0 0 1 ${x1.toFixed(
    2,
  )},${y1.toFixed(2)} Z`;
}


/** Вібрація не має права зупинити анімацію: у деяких клієнтах Telegram
 *  метод кидає виняток, а він летить із rAF-циклу і вбиває оберт. */
function haptic(fn: () => void): void {
  try {
    fn();
  } catch {
    /* ігноруємо */
  }
}


export function WheelPage() {
  const navigate = useNavigate();
  const state = useWheelState();
  const spinMut = useWheelSpin();
  const refreshWheel = useRefreshWheel();
  const [result, setResult] = useState<Prize | null>(null);
  const [packsOpen, setPacksOpen] = useState(false);
  // Подвоєння призу (owner 2026-10-25; з 04.11 — за підписку на Instagram inFLOW): номер оберту, який подвоюємо.
  const [doubleSpin, setDoubleSpin] = useState<number | null>(null);
  // Номер оберту, приз якого зараз у попапі. last_prize зі стану після оберту оновлюється із затримкою — за ним
  // сервер міг би зафіксувати «пропозицію місяця» на попередньому оберті (раз на місяць, owner 2026-10-26).
  const [resultSpin, setResultSpin] = useState<number | null>(null);
  const purchase = useWheelPurchase();
  const packs = state.data?.packs ?? [];
  const spinsLeft = state.data?.spins_left ?? (state.data?.available ? 1 : 0);
  // Ціна одного оберту — пакет на 1 оберт (для рядка «Ще оберти»).
  const oneSpinPrice =
    packs.find((p) => p.spins === 1)?.amount ??
    (packs.length ? Math.min(...packs.map((p) => p.amount)) : 49);

  /** Рахунок на пакет і одразу оплата: зайвий екран між бажанням і оплатою
   *  коштує конверсії (owner 2026-09-23). */
  const buySpins = async (slug: string) => {
    try {
      const res = await purchase.mutateAsync(slug);
      // Після оплати покажемо «Оберти вже твої» (WheelPurchaseWatcher).
      markWheelPurchasePending();
      const webapp = tg();
      if (webapp?.openLink) webapp.openLink(res.url);
      else window.open(res.url, "_blank", "noopener,noreferrer");
      setPacksOpen(false);
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(`Не вдалося створити рахунок: ${(e as Error).message}`);
    }
  };
  const [noSpinOpen, setNoSpinOpen] = useState(false);
  const spinning = useRef(false);
  // Якщо оберту немає — кажемо це одразу, не змушуючи людину тиснути
  // «Крутити» (owner 2026-09-05: «колесо не відкриває, але й попап не
  // показує»). Спрацьовує один раз на вхід.
  // Застосунок закрився чи вилетів просто на попапі призу — подвоєння не згорає (owner 2026-11-04): поки сервер
  // каже «open» (3 години від виграшу, «Пропустити» людина не натискала), під колесом рядок, що відкриває вікно.
  const lastPrize = state.data?.last_prize;
  const rowSpinId =
    !result && doubleSpin === null && lastPrize?.id && lastPrize.spun_at &&
    Date.now() - Date.parse(lastPrize.spun_at) < 3 * 3600_000
      ? lastPrize.id
      : null;
  const rowDq = useWheelDouble(rowSpinId);
  const doubleRow = rowSpinId !== null && rowDq.data?.status === "open" ? rowDq.data : null;
  const noSpinShown = useRef(false);
  //: Людина вже крутила на цьому екрані — попап «недоступний» більше не має
  //: права спливати, навіть коли сервер скаже, що обертів немає.
  const spunHere = useRef(false);
  // Лист бота «оберти не оплачено» веде сюди з `?packs=1` (owner 2026-10-20):
  // одразу відкриваємо «Ще оберти». Стоїть ПЕРЕД ефектами попапів на вході —
  // так на вхід лишається один попап, а не пакети поверх «недоступний».
  const packsFromBot = useRef(false);
  useEffect(() => {
    if (packsFromBot.current || !state.data) return;
    packsFromBot.current = true;
    if (!new URLSearchParams(window.location.search).has("packs")) return;
    if ((state.data.packs ?? []).length === 0) return;
    noSpinShown.current = true;
    setPacksOpen(true);
  }, [state.data]);
  // Топ перемог місяця (owner 2026-10-11): сервер уже віддає ≤10 рядків,
  // від найціннішого призу до найдешевшого, при рівності — новіший вище.
  const topMonth = state.data?.top_month ?? [];
  // Час оберту, приз якого зараз у попапі: закрили попап — приз побачено.
  const shownSpunAt = useRef<string | null>(null);
  const qc = useQueryClient();
  // Попап призу на вході в колесо — замість «Оберт зараз недоступний»:
  // 1) прихід за кнопкою «Забрати приз» із листа (`?claim`, баг 05.09);
  // 2) приз, якого людина не побачила (05.10.2026): колесо крутилось без
  //    зупинки чи застосунок закрили посеред оберту, приз нараховано, а попапа
  //    не було, і на вході людина бачила лише «Оберт зараз недоступний».
  // ⚠️ На вхід — не більше ОДНОГО попапа: цей ефект стоїть ПЕРЕД ефектом
  // «недоступний» і закриває його через `noSpinShown` (owner 2026-09-05:
  // «і знову було 2 попапи»). Після оберту на цьому екрані приз показує сам
  // оберт, тому тут — лише вхід.
  useEffect(() => {
    if (noSpinShown.current || spinning.current || spunHere.current) return;
    if (result !== null) return;
    const lp = state.data?.last_prize;
    if (!state.data || !lp) return;
    const prize = prizeByKey(lp.prize_key);
    if (!prize) return;
    const wantsClaim = new URLSearchParams(window.location.search).has("claim");
    const unseen = !!lp.spun_at && isFreshSpin(lp.spun_at) && !prizeSeen(lp.spun_at);
    if (!wantsClaim && !unseen) return;
    noSpinShown.current = true;
    // Подвоєння цього призу вже розпочате (людина ходила в Instagram) — замість попапу призу відкриється вікно
    // подвоєння (ефект нижче); два попапи один на одному не показуємо.
    if (state.data.double_pending?.spin_id === lp.id) return;
    // Людина вже відкрила інший попап (пакети, «недоступний»), або чекає
    // попап «Оберти вже твої» після покупки — свій не накладаємо і не
    // показуємо слідом: приз спливе на наступному вході, двох підряд не буде.
    if (packsOpen || noSpinOpen) return;
    if (!wantsClaim && purchasePopupPending(state.data.purchase?.order_reference)) return;
    shownSpunAt.current = lp.spun_at ?? null;
    // Під попапом «Колесо зупинилось на твоєму призі» стрілка теж стоїть на
    // ньому — інакше після закриття видно інший сектор.
    angleRef.current = restAngle(SERVER_PRIZE_KEYS.indexOf(lp.prize_key));
    if (diskRef.current) diskRef.current.style.transform = `rotate(${angleRef.current}deg)`;
    if (!wantsClaim) {
      reportDiag("wheel_prize_recovered", {
        via: "entry",
        prize: lp.prize_key,
        age_min: lp.spun_at ? Math.round((Date.now() - Date.parse(lp.spun_at)) / 60000) : null,
      });
    }
    setResultSpin(lp.id ?? null);
    setResult(prize);
  }, [state.data, result, packsOpen, noSpinOpen]);
  // Одна позначка часу на рендер — щоб усі рядки рахувались від однієї миті.
  const renderedAt = useMemo(() => Date.now(), [state.data]);
  const diskRef = useRef<SVGGElement | null>(null);
  const pinRef = useRef<SVGGElement | null>(null);
  const angleRef = useRef(0);
  const serverPrize = useRef<WheelSpinOut | null>(null);
  // Номер поточного оберту: цикл кадрів старого оберту (чи покинутої
  // сторінки) бачить, що номер змінився, і зупиняється сам.
  const runId = useRef(0);
  const mounted = useRef(true);
  // Прибирання поточного оберту (слухач видимості) — щоб викликати і з
  // кінця оберту, і з виходу зі сторінки.
  const spinCleanup = useRef<(() => void) | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Пішли зі сторінки посеред оберту: цикл кадрів більше не крутиться
      // у фоні (раніше він жив до закриття застосунку разом із вібрацією).
      runId.current += 1;
      spinCleanup.current?.();
    };
  }, []);

  useEffect(() => {
    if (noSpinShown.current) return;
    // ⚠️ Тільки на вході й тільки поки нічого не крутиться. Після оберту
    // стан стає available=false, і без цієї перевірки попап накривав колесо
    // просто під час обертання (знайдено аудитом перед запуском).
    // ⚠️ `spunHere` — головна умова. Без неї попап спливав ОДРАЗУ ПІСЛЯ
    // закриття попапа з призом: людина закривала приз, `result` ставав null,
    // ефект перезапускався і бачив available=false (owner 2026-09-05: «і знову
    // було 2 попапи»). Хто крутив на цьому екрані — попапа не бачить узагалі.
    if (spinning.current || spunHere.current || result !== null) return;
    // Людина повернулась по подвоєння (застосунок закрився на попапі призу) — «недоступний» не накриває рядок
    // «Подвоїти приз» (owner 2026-11-04). Поки стан подвоєння вантажиться — чекаємо.
    if (rowSpinId !== null && (rowDq.isLoading || doubleRow)) return;
    if (state.data && state.data.available === false) {
      noSpinShown.current = true;
      setNoSpinOpen(true);
    }
  }, [state.data, result, rowSpinId, rowDq.isLoading, doubleRow]);

  const sectors = useMemo(
    () =>
      PRIZES.map((p, i) => {
        const mid = -90 + (i + 0.5) * STEP;
        const flip = ((mid % 360) + 360) % 360 > 90 && ((mid % 360) + 360) % 360 < 270;
        return {
          ...p,
          i,
          d: sectorPath(i),
          rotate: flip ? mid + 180 : mid,
          x: flip ? -R_LABEL : R_LABEL,
        };
      }),
    [],
  );

  // Замір підписів — тільки коли шрифти вже завантажені, інакше канвас
  // міряє запасний шрифт і центр виходить хибний.
  const [labelFit, setLabelFit] = useState<{ y: number; dx: number }[] | null>(null);
  useEffect(() => {
    let alive = true;
    const run = () => {
      if (!alive) return;
      setLabelFit(PRIZES.map((p) => measureLabel(p.l1, p.l2)));
    };
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.ready) {
      Promise.all([
        fonts.load("800 16px Unbounded", "0123456789 днів"),
        fonts.load("700 10px Manrope", "ПІДПИСКИ"),
      ])
        .catch(() => undefined)
        .then(() => fonts.ready)
        .then(run, run);
    } else {
      run();
    }
    return () => {
      alive = false;
    };
  }, []);

  /** Під яким сектором зараз стрілка (стрілка вгорі, тобто на -90°). */
  const sectorAt = (deg: number): number => {
    // Диск повернули на deg → сектор i займає [-90 + i*STEP + deg ...].
    // Кут від стрілки до початку нульового сектора:
    const rel = (((-deg % 360) + 360) % 360) / STEP;
    return Math.floor(rel) % N;
  };

  /**
   * Оберт як у житті: задаємо початкову швидкість і відпускаємо. Далі
   * рахуємо тертя й УДАРИ ОБ КІЛЬКИ на межах секторів (owner 2026-09-01:
   * «воно крутиться, вдаряючись об ці кружечки»).
   *
   * Ніякої заздалегідь обраної цілі немає — де зупиниться, той приз і
   * випав. Тому колесо може стати серединою сектора, майже на межі, а може
   * зачепитись за кульку й не перевалити останній міліметр.
   */
  /**
   * ОДНА фізика на весь оберт. Стрілка лежить на диску, кулька в куті сектора
   * піднімає її як гірка: на підйомі гальмує диск, на спуску підштовхує.
   * Колесо рухається ТІЛЬКИ вперед — не відскакує і не гойдається.
   *
   * ⚠️ Крок фізики ФІКСОВАНИЙ (1/60 с), а не «скільки минуло між кадрами».
   * Інакше той самий поштовх дає різний результат на різних телефонах — і
   * колесо зупиняється не на тому секторі, який прислав сервер (owner
   * 2026-09-04: «мені 200 бонусів випало, а показало два місяці»).
   */
  const spin = async () => {
    if (spinning.current) return;
    if (state.data && !state.data.available) {
      tg()?.HapticFeedback?.notificationOccurred?.("warning");
      setNoSpinOpen(true);
      return;
    }
    spinning.current = true;
    spunHere.current = true;
    setResult(null);
    serverPrize.current = null;
    haptic(() => tg()?.HapticFeedback?.impactOccurred?.("medium"));

    // Кожен оберт має свій номер: цикл кадрів живе, лише поки номер його.
    const run = ++runId.current;
    const alive = () => mounted.current && runId.current === run;
    const started = performance.now();
    const elapsed = () => Math.round(performance.now() - started);
    // Приз, який людина вже мала до оберту, — щоб після збою впізнати новий.
    const prevSpunAt = state.data?.last_prize?.spun_at ?? null;
    // Оберт уже закінчився (приз показано або зупинились без нього) —
    // друге закінчення від сторожа чи від кадру нічого не робить.
    let settled = false;
    let phase: "coast" | "physics" | "glide" = "coast";
    let hiddenReported = false;
    reportDiag("wheel_spin_start", {
      spins_left: state.data?.spins_left ?? null,
      available: state.data?.available ?? null,
    });
    // Людина згорнула чи закрила застосунок посеред оберту: так ми бачимо,
    // що приз лишився непоказаним (05.10 саме так і було).
    const onVisibility = () => {
      if (document.visibilityState !== "hidden" || settled || hiddenReported) return;
      hiddenReported = true;
      reportDiag("wheel_hidden", { ms: elapsed(), phase });
    };
    document.addEventListener("visibilitychange", onVisibility);
    const onUnmount = () => {
      if (!settled) {
        reportDiag("wheel_stop", { reason: "unmount", ms: elapsed(), phase });
        // Приз уже нараховано, а попап не показано: свіжий стан покаже його
        // на наступному вході в колесо, а головна — правильний лічильник.
        void refreshWheel();
      }
      cleanup();
    };
    const cleanup = () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearTimeout(watchdog);
      if (spinCleanup.current === onUnmount) spinCleanup.current = null;
    };
    spinCleanup.current = onUnmount;

    // ⚠️ Запит до сервера НЕ тримає анімацію. Раніше колесо стояло, поки
    // прилетить відповідь, — власник 2026-09-05: «від натискання до обертання
    // секунди дві, неприємна затримка». Тепер диск зривається з місця в ту ж
    // мить: спершу крутиться рівно, а щойно сервер називає сектор — переходимо
    // на звичайну фізику з поштовхом, підібраним ВІД ПОТОЧНОГО кута.
    let target: number | null = null;
    let failed = false;
    let failure: unknown = null;
    const requestedAt = performance.now();
    const serverSettled = spinMut
      .mutateAsync()
      .then((res) => {
        // Сектор звіряємо: без нього фізика не має куди везти колесо, а
        // NaN у куті тихо ламав і анімацію, і попап.
        const sector =
          Number.isInteger(res.sector) && res.sector >= 0 && res.sector < N
            ? res.sector
            : SERVER_PRIZE_KEYS.indexOf(res.prize_key);
        serverPrize.current = { ...res, sector };
        if (res.spin_id) prefetchWheelDouble(qc, res.spin_id);
        if (sector >= 0) target = sector;
        else failed = true;
        reportDiag("wheel_server", {
          ok: true,
          ms: Math.round(performance.now() - requestedAt),
          prize: res.prize_key,
          sector,
        });
      })
      .catch((e: unknown) => {
        failed = true;
        failure = e;
        reportDiag("wheel_server", {
          ok: false,
          ms: Math.round(performance.now() - requestedAt),
          status: e instanceof ApiError ? e.status : null,
        });
      });

    const DT = 1 / 60;
    const MAX_LIFT = 22;
    const PEG_HALF = 7;
    const SPRING = 68;
    // Гальмування. Owner 2026-09-04: «то, сколько он крутился, это была
    // идеальная длина, которая создаёт ощущение азарта» — тому лишаємо довгий
    // оберт (~11 с, близько 11 кіл). Скорочення до 5 с він забракував.
    const VISC = 0.26;
    const DRY = 2;
    // ⚠️ Стеля фізики (05.10.2026). Крок фіксований (1/60 с), а кулька — вузька
    // (14°). На деяких швидкостях крок «перестрибує» підйом на кульку і щоразу
    // потрапляє на спуск: поштовх вниз рівно гасить тертя, і колесо крутиться
    // ВІЧНО — 100-700 °/с, з вібрацією на кожній кульці. Так застрягав приблизно
    // кожен 55-й оберт (1,8 % у симуляції на 20 000 обертів). Звичайний оберт
    // стає за 9-14 с (≈860 кроків), тому поштовх, після якого колесо не стало
    // за 30 с, ми просто не беремо, а анімація сама не крутить довше за стелю.
    const PHYS_MAX_STEPS = 1800;

    /** Один крок фізики. Спільний для симуляції і для анімації. */
    const step = (th: number, om: number): [number, number] => {
      const ph = (((th / STEP) % 1) + 1) % 1;
      const signed = (ph < 0.5 ? ph : ph - 1) * STEP;
      const d = Math.abs(signed);
      let slope = 0;
      if (d < PEG_HALF) {
        const k = 1 - d / PEG_HALF;
        slope = ((2 * MAX_LIFT * k) / PEG_HALF) * (signed < 0 ? 1 : -1);
      }
      let o = om - SPRING * slope * DT;
      o -= (VISC * o + DRY) * DT;
      if (o < 0) o = 0;
      return [th + o * DT, o];
    };

    const liftAt = (th: number): number => {
      const ph = (((th / STEP) % 1) + 1) % 1;
      const signed = (ph < 0.5 ? ph : ph - 1) * STEP;
      const d = Math.abs(signed);
      if (d >= PEG_HALF) return 0;
      const k = 1 - d / PEG_HALF;
      return MAX_LIFT * k * k;
    };

    /** Куди прийде колесо з таким поштовхом. Кроки ті самі, тож результат
     *  збігається з анімацією до градуса. `null` — колесо за стелю не стало
     *  (застрягло на кульках), такий поштовх брати не можна. */
    const landing = (v0: number, from: number): number | null => {
      let th = from;
      let om = v0;
      for (let i = 0; i < PHYS_MAX_STEPS && om > 0; i++) [th, om] = step(th, om);
      return om > 0 ? null : th;
    };

    /** Поставити диск рівно на сектор — коли анімації вже не буде. */
    const placeOn = (sector: number) => {
      const centre = restAngle(sector);
      angleRef.current = centre;
      if (diskRef.current) diskRef.current.style.transform = `rotate(${centre}deg)`;
      if (pinRef.current) pinRef.current.style.transform = "rotate(0deg)";
    };

    /** Показати приз сервера попапом. Попап і є «побачив»: від нього
     *  позначаємо оберт, інакше після оновлення стану той самий приз спливе
     *  вдруге як непоказаний. */
    const showServerPrize = (sp: WheelSpinOut) => {
      const local = PRIZES[sp.sector];
      // Підпис беремо з нашої таблиці, але звіряємо із сервером: нараховує
      // ВІН, тож якщо колись таблиці розійдуться, людині треба показати те,
      // що вона реально отримала (аудит перед запуском 05.09).
      // Звіряємо за КЛЮЧЕМ призу. Раніше порівнювали число з підписом, а в
      // «Ще один оберт», «Місяць» і «Рік» підпис не число — вважалось
      // розбіжністю, і попап показував «оберт» чи «365 днів підписки»
      // (owner 2026-09-23).
      const drift = sp.prize_key !== SERVER_PRIZE_KEYS[sp.sector];
      markPrizeSeen(sp.spun_at);
      shownSpunAt.current = sp.spun_at ?? null;
      setResultSpin(sp.spin_id ?? null);
      setResult(
        drift
          ? {
              ...local,
              kind: sp.kind as Prize["kind"],
              value: String(sp.amount || ""),
              unit:
                sp.kind === "bonus"
                  ? "бонусів"
                  : sp.kind === "days"
                    ? "днів підписки"
                    : local.unit,
            }
          : local,
      );
      haptic(() => tg()?.HapticFeedback?.notificationOccurred?.("success"));
    };

    // Якщо анімація з якоїсь причини не дійде до кінця (згорнули застосунок,
    // кадри не малюються), оберт закінчується сам: приз сервера — попапом,
    // без нього — чесне пояснення. Раніше сторож лише вмикав кнопку назад, а
    // колесо крутилось далі (owner 2026-09-04, 05.10).
    const watchdog = window.setTimeout(() => {
      if (settled) return;
      const sp = serverPrize.current;
      if (sp && sp.sector >= 0) {
        reportDiag("wheel_stop", { reason: "watchdog", ms: elapsed(), phase, prize: true });
        settled = true;
        runId.current += 1;
        cleanup();
        spinning.current = false;
        placeOn(sp.sector);
        if (mounted.current) showServerPrize(sp);
        void refreshWheel();
        return;
      }
      void stopWithoutPrize("watchdog");
    }, 60000);

    const finish = (th: number, how: "physics" | "glide" | "exception") => {
      if (settled) return;
      settled = true;
      cleanup();
      // Кут тримаємо в межах кола. Інакше з кожним обертом він росте, і
      // підбір поштовху для наступного разу стартує з іншої точки —
      // саме через це другий оберт міг не знайти сектор (owner 2026-09-04:
      // «нажал крутить еще раз — оно зависло»).
      angleRef.current = Number.isFinite(th) ? ((th % 360) + 360) % 360 : 0;
      reportDiag("wheel_finish", { ms: elapsed(), how, prize: serverPrize.current?.prize_key ?? null });
      const sp = serverPrize.current;
      // Кадр упав посеред оберту — ставимо диск на приз, щоб стрілка
      // показувала те саме, що попап.
      if (how === "exception" && sp && sp.sector >= 0) placeOn(sp.sector);
      window.setTimeout(() => {
        spinning.current = false;
        void refreshWheel();
        // Пішли зі сторінки за цю секунду — приз лишається непоказаним, і
        // його покаже вхід у колесо.
        if (!mounted.current) return;
        if (sp && sp.sector >= 0) showServerPrize(sp);
      }, 1200);
    };

    /** Сервер не відповів, відповів помилкою або оберт зупинив сторож.
     *  Колесо стає, а людина чує правду, а не бачить застиглий диск
     *  (owner 2026-09-23: «крутилось без кінця і не зупинялось»).
     *
     *  ⚠️ «Оберт не списано» можна казати лише тоді, коли сервер справді
     *  відмовив. Відповідь могла загубитись у дорозі (05.10: сервер віддав
     *  приз за 3,3 с, а людина його так і не побачила), тому спершу
     *  дочікуємось запиту й перечитуємо стан: зʼявився новий приз — показуємо
     *  його попапом. */
    const stopWithoutPrize = async (
      reason: "server_fail" | "timeout30" | "watchdog" | "exception",
    ) => {
      if (settled) return;
      settled = true;
      runId.current += 1;
      cleanup();
      reportDiag("wheel_stop", {
        reason,
        ms: elapsed(),
        phase,
        status: failure instanceof ApiError ? failure.status : null,
      });
      // Запит обмежений таймаутом клієнта (15 с), тож чекаємо недовго.
      await serverSettled;
      const sp = serverPrize.current;
      if (sp && sp.sector >= 0) {
        spinning.current = false;
        placeOn(sp.sector);
        if (mounted.current) showServerPrize(sp);
        void refreshWheel();
        return;
      }
      try {
        await refreshWheel();
      } catch {
        /* стан не оновився — нижче чесне «якщо встиг пройти» */
      }
      spinning.current = false;
      if (!mounted.current) return;
      const lp = qc.getQueryData<WheelStateOut>(["wheel"])?.last_prize;
      const recovered =
        lp?.spun_at && lp.spun_at !== prevSpunAt && !prizeSeen(lp.spun_at)
          ? prizeByKey(lp.prize_key)
          : null;
      if (lp?.spun_at && recovered) {
        placeOn(SERVER_PRIZE_KEYS.indexOf(lp.prize_key));
        markPrizeSeen(lp.spun_at);
        shownSpunAt.current = lp.spun_at;
        reportDiag("wheel_prize_recovered", { via: reason, prize: lp.prize_key, age_min: 0 });
        setResultSpin(lp.id ?? null);
        setResult(recovered);
        haptic(() => tg()?.HapticFeedback?.notificationOccurred?.("success"));
        return;
      }
      const status = failure instanceof ApiError ? failure.status : null;
      if (status === 409) {
        // Сервер каже, що оберту немає, — це той самий попап, що й на вході.
        setNoSpinOpen(true);
        return;
      }
      // Звʼязок чи проксі: запит міг дійти до сервера, тож про «не списано»
      // мовчимо. Відмова самого застосунку (500, 4xx) — оберт точно не списано.
      const unsure = status === null || status === 0 || status === 502 || status === 503 || status === 504;
      showAppAlert(
        unsure
          ? "Звʼязок пропав посеред оберту. Якщо він устиг пройти, приз уже нараховано — перевір підписку і баланс бонусів."
          : "Не вдалося докрутити колесо — оберт не списано. Спробуй ще раз.",
      );
    };

    /** Запасний шлях: рівна дотяжка до центру сектора, якщо поштовх не
     *  підібрався. Краще коротка чесна дотяжка, ніж приз не з тим сектором. */
    const glideTo = (from: number, sector: number) => {
      phase = "glide";
      const centre = -(sector * STEP + STEP / 2) - 4;
      const base = from - (((from % 360) + 360) % 360);
      const finalAngle = base + 360 * 2 + ((centre - (base % 360)) % 360);
      const t0 = performance.now();
      const glide = (now: number) => {
        if (!alive() || settled) return;
        try {
          const k = Math.min(1, Math.max(0, (now - t0) / 2600));
          const eased = 1 - Math.pow(1 - k, 3);
          const th = from + (finalAngle - from) * eased;
          if (diskRef.current) diskRef.current.style.transform = `rotate(${th}deg)`;
          if (pinRef.current)
            pinRef.current.style.transform = `rotate(${-liftAt(th)}deg)`;
          if (k < 1) {
            window.requestAnimationFrame(glide);
            return;
          }
          angleRef.current = th;
          finish(th, "glide");
        } catch {
          finish(finalAngle, "exception");
        }
      };
      window.requestAnimationFrame(glide);
    };

    /** Підбір поштовху під сектор сервера — від того кута, де диск ЗАРАЗ.
     *  Беремо лише поштовх, після якого колесо гарантовано стає (`landing`
     *  не null): анімація повторює ті самі кроки, тож стане й вона. */
    const impulseFor = (from: number, sector: number): number | null => {
      const start = 1100 + Math.random() * 60;
      for (let v = start; v < 2100; v += 0.5) {
        const th = landing(v, from);
        if (th !== null && sectorAt(th) === sector) return v;
      }
      return null;
    };

    let theta = angleRef.current;
    let pinAng = 0;
    let lastPeg = Math.floor(theta / STEP);
    let acc = 0;
    let last = performance.now();
    // Фаза 1 — рівний хід, поки чекаємо сервер. Фаза 2 — звичайна фізика.
    let coasting = true;
    let omega = 1500;
    let physSteps = 0;

    const frame = (now: number) => {
      acc += now - last;
      last = now;
      // Поки сервер мовчить — крутимо рівно, без гальмування. Щойно сектор
      // відомий, підбираємо поштовх ВІД ПОТОЧНОГО кута і віддаємо диск фізиці.
      if (coasting && target !== null) {
        const v = impulseFor(theta, target);
        if (v !== null) {
          omega = v;
          coasting = false;
          phase = "physics";
        } else {
          // Не знайшли поштовх — доводимо дотяжкою (нижче), щоб приз на екрані
          // збігся з тим, що прислав сервер.
          coasting = false;
          omega = 0;
          glideTo(theta, target);
          return;
        }
      }
      if (coasting && failed) {
        // Сервер відповів помилкою (або без сектора).
        void stopWithoutPrize("server_fail");
        return;
      }
      if (coasting && performance.now() - started > 30000) {
        // Відповіді немає пів хвилини (зник інтернет).
        void stopWithoutPrize("timeout30");
        return;
      }

      let moving = false;
      while (acc >= DT * 1000) {
        acc -= DT * 1000;
        if (coasting) {
          theta += omega * DT;
          moving = true;
        } else {
          const [th, om] = step(theta, omega);
          theta = th;
          omega = om;
          physSteps += 1;
          if (om > 0) moving = true;
        }
        const om = omega;
        const peg = Math.floor(theta / STEP);
        if (peg !== lastPeg) {
          lastPeg = peg;
          haptic(() =>
            tg()?.HapticFeedback?.impactOccurred?.(om > 240 ? "light" : "medium"),
          );
        }
      }

      const targetLift = liftAt(theta);
      pinAng +=
        (targetLift - pinAng) *
        Math.min(1, (targetLift > pinAng ? 26 : 11) * ((now - last + 16) / 1000));

      if (diskRef.current) diskRef.current.style.transform = `rotate(${theta}deg)`;
      if (pinRef.current) pinRef.current.style.transform = `rotate(${-pinAng}deg)`;

      if (!coasting && omega > 0 && physSteps > PHYS_MAX_STEPS && target !== null) {
        // Стеля: так бути не повинно (поштовх перевірено), але якщо колесо
        // все ж не стало — доводимо його до призу сервера дотяжкою.
        reportDiag("wheel_stop", { reason: "physics_cap", ms: elapsed(), phase });
        omega = 0;
        glideTo(theta, target);
        return;
      }
      if (moving || omega > 0) {
        window.requestAnimationFrame(safeFrame);
        return;
      }
      angleRef.current = theta;
      finish(theta, "physics");
    };

    const safeFrame = (now: number) => {
      if (!alive() || settled) return;
      try {
        frame(now);
      } catch {
        // Кадр упав. Приз сервера вже відомий — показуємо його; ні —
        // дочікуємось сервера і вирішуємо там.
        if (serverPrize.current && serverPrize.current.sector >= 0) finish(theta, "exception");
        else void stopWithoutPrize("exception");
      }
    };
    window.requestAnimationFrame(safeFrame);
  };

  /** Попап призу закрили будь-якою кнопкою — приз побачено, на вході в
   *  колесо він більше не спливе. */
  // Подвоєння (owner 2026-10-25): лише в моменті — кнопка в попапі призу; закрити попап без подвоєння = спершу
  // «Точно не подвоювати?». Розпочате й не завершене (людина пішла в Instagram) відкривається знову саме.
  const lastSpinId = resultSpin ?? state.data?.last_prize?.id ?? null;
  const dq = useWheelDouble(result && lastSpinId ? lastSpinId : null);
  const canDouble = !!result && dq.data?.status === "open";
  // Попап виграшу показуємо вже з обома кнопками: чекаємо стан подвоєння (зазвичай він уже в кеші — див.
  // prefetchWheelDouble), але не довше 1,5 с — гейт рендеру мусить мати стелю.
  const [gateOver, setGateOver] = useState(false);
  useEffect(() => {
    if (!result) {
      setGateOver(false);
      return;
    }
    const t = window.setTimeout(() => setGateOver(true), 1500);
    return () => window.clearTimeout(t);
  }, [result]);
  const prizeReady = !lastSpinId || !dq.isLoading || gateOver;
  const [skipAsk, setSkipAsk] = useState<null | (() => void)>(null);
  const [skipBusy, setSkipBusy] = useState(false);
  const guard = (action: () => void) => () => {
    if (canDouble) setSkipAsk(() => action);
    else action();
  };
  const resumed = useRef(false);
  useEffect(() => {
    const dp = state.data?.double_pending;
    if (!dp || resumed.current || result) return;
    resumed.current = true;
    setDoubleSpin(dp.spin_id);
  }, [state.data?.double_pending, result]);
  // Лист «Спробувати ще раз» після відмови веде сюди з `?double=<оберт>` (owner 2026-11-04): відкриваємо вікно
  // подвоєння цього оберту; чи можна ще подвоїти — вирішує сервер, вікно саме покаже стан.
  const fromLetter = useRef(false);
  useEffect(() => {
    if (fromLetter.current) return;
    fromLetter.current = true;
    const id = Number(new URLSearchParams(window.location.search).get("double"));
    if (Number.isInteger(id) && id > 0) setDoubleSpin(id);
  }, []);

  const closePrize = () => {
    markPrizeSeen(shownSpunAt.current);
    shownSpunAt.current = null;
    setResult(null);
  };

  return (
    <div className="pb-2">
      <BackNav showMenu />
      <PageHeader
        title="Колесо фортуни"
        eyebrow="Раз на місяць"
        titleLead="Колесо"
        titleAccent="фортуни"
        scribbleWidth={150}
        actions={
          // Лічильник обертів (owner 2026-09-24): скільки ще можна крутити.
          spinsLeft > 0 ? (
            <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 font-display text-[0.65625rem] font-bold text-white">
              {spinsLeft === 1 && (state.data?.free_left ?? 0) === 1
                ? "1 безкоштовний оберт"
                : spinsLabel(spinsLeft)}
            </span>
          ) : undefined
        }
        subtitle={
          state.data && !state.data.available
            ? "Наступний безкоштовний оберт даємо 5 числа."
            : "Один безкоштовний оберт на місяць. Порожніх секторів немає — щось отримаєш завжди."
        }
      />

      {/* Колесо на всю ширину екрана: -mx-5 гасить відступ Layout, а запас у
          viewBox лишається рівно під тінь диска (owner 2026-09-01: «колесо
          явно можна збільшити»). */}
      <div className="relative -mx-5 -mt-2">
        {/* Верхній край полотна тримаємо впритул до стрілки: у viewBox було
              52 одиниці порожнечі згори (~30px на екрані), і саме вона робила
              розрив між описом і колесом (owner 2026-09-03). */}
          <svg viewBox="-26 -20 572 584" className="block w-full">
          <defs>
            <radialGradient id="whBez" cx="34%" cy="24%" r="82%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="58%" stopColor="#F6F0E6" />
              <stop offset="100%" stopColor="#E6DCCB" />
            </radialGradient>
            <radialGradient id="whFace" cx="36%" cy="26%" r="80%">
              <stop offset="0%" stopColor="#FFFDFA" />
              <stop offset="100%" stopColor="#F4EDE2" />
            </radialGradient>
            <linearGradient id="whWin" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FFA135" />
              <stop offset="100%" stopColor="#FE6B04" />
            </linearGradient>
            <radialGradient id="whPeg" cx="34%" cy="30%" r="80%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#EDE4D5" />
            </radialGradient>
            <radialGradient id="whHub" cx="34%" cy="26%" r="78%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#F4EDE2" />
            </radialGradient>
            <filter id="whDrop" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#3B2A18" floodOpacity="0.18" />
            </filter>
            <filter id="whHubS" x="-60%" y="-60%" width="220%" height="220%">
              <feDropShadow dx="0" dy="5" stdDeviation="7" floodColor="#3B2A18" floodOpacity="0.22" />
            </filter>
          </defs>

          {/* Обід повертаємо (owner 2026-09-01: «поверни те колесо, що було»),
              але ТОНКИЙ: між ним і призами більше немає порожньої смуги, яка
              зʼїдала трикутники. Зайву волосяну обводку теж прибрано. */}
          <circle cx={C} cy={C} r={R_OUT + 10} fill="url(#whBez)" filter="url(#whDrop)" />
          <circle cx={C} cy={C} r={R_OUT + 10} fill="none" stroke="rgba(23,20,17,.10)" />

          {/* обертається лише диск: обід, кнопка і стрілка стоять */}
          <g ref={diskRef} style={{ transformOrigin: `${C}px ${C}px` }}>
            <circle cx={C} cy={C} r={R_OUT} fill="url(#whFace)" />
            {sectors.map((s) => (
              <path
                key={`s${s.i}`}
                d={s.d}
                fill={
                  s.best
                    ? "url(#whWin)"
                    : s.good
                      ? "#FFF1E0"
                      : SHADES[s.i % SHADES.length]
                }
              />
            ))}
            {/* Дві волосяні окружності — як шкала на приладі. Саме вони
                дають відчуття точності, а не декору. */}
            <circle cx={C} cy={C} r={R_IN + 14} fill="none" stroke="rgba(23,20,17,.07)" />
            <circle cx={C} cy={C} r={R_OUT - 16} fill="none" stroke="rgba(23,20,17,.05)" />

            {/* Джекпот і «1+1» позначені тонкою дугою, а не заливкою — це
                тримає мінімалізм і водночас виділяє головні сектори. */}
            {sectors
              .filter((x) => x.best || x.good)
              .map((x) => (
                <path
                  key={`hi${x.i}`}
                  d={arcPath(R_OUT - 5, x.i)}
                  fill="none"
                  stroke={x.best ? "#FFFFFF" : "#FE6B04"}
                  strokeOpacity={x.best ? 0.75 : 0.55}
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              ))}

            {sectors.map((s) => {
              const [x, y] = pol(R_OUT, -90 + s.i * STEP);
              return (
                <line
                  key={`l${s.i}`}
                  x1={C}
                  y1={C}
                  x2={x.toFixed(2)}
                  y2={y.toFixed(2)}
                  stroke="rgba(23,20,17,.08)"
                />
              );
            })}
            {sectors.map((s) => (
              <g key={`t${s.i}`} transform={`translate(${C},${C}) rotate(${s.rotate})`}>
                {/* Обидва рядки — в одному <text> через tspan з тим самим x.
                    Так вони гарантовано на одній осі й паралельні: окремі
                    <text> давали відчуття перекосу (owner 2026-09-02). */}
                <text
                  x={s.x + (labelFit ? labelFit[s.i].dx : 0)}
                  y={labelFit ? labelFit[s.i].y : -4}
                  textAnchor="middle"
                  fill={s.best ? "#FFFFFF" : s.good ? "#D05703" : "#171411"}
                >
                  <tspan
                    x={s.x + (labelFit ? labelFit[s.i].dx : 0)}
                    fontFamily="Unbounded"
                    fontSize={l1Size(s.l1)}
                    fontWeight={800}
                    letterSpacing="-0.02em"
                  >
                    {s.l1}
                  </tspan>
                  <tspan
                    x={s.x + (labelFit ? labelFit[s.i].dx : 0)}
                    dy={L2_GAP}
                    fontFamily="Manrope"
                    fontSize={L2_SIZE}
                    fontWeight={700}
                    letterSpacing="0.06em"
                    fill={
                      s.best
                        ? "rgba(255,255,255,.85)"
                        : s.good
                          ? "rgba(208,87,3,.75)"
                          : "rgba(23,20,17,.48)"
                    }
                  >
                    {s.l2.toUpperCase()}
                  </tspan>
                </text>
              </g>
            ))}
            {/* Обвід диска малюємо ДО кульок — інакше він проходив рівно
                через їхні центри, і кулька виглядала прозорою
                (owner 2026-09-02: «вони сука прозорі»). */}
            <circle cx={C} cy={C} r={R_OUT} fill="none" stroke="rgba(23,20,17,.10)" strokeWidth={1.5} />

            {/* Кульки — рівно в КУТАХ секторів, на самому краю диска. Саме за
                їхній край чіпляється носик стрілки, яка проходить зверху. */}
            {PRIZES.map((_, i) => {
              const [x, y] = pol(R_OUT, -90 + i * STEP);
              return (
                <g key={`peg${i}`}>
                  <circle cx={x.toFixed(2)} cy={y.toFixed(2)} r={7} fill="#8C7C63" />
                  <circle cx={x.toFixed(2)} cy={y.toFixed(2)} r={5.6} fill="url(#whPeg)" />
                </g>
              );
            })}
          </g>

          {/* Кнопка «Крутити» — у центрі колеса (owner 2026-09-01). Ступиця
              не обертається, інакше напис їхав би разом із диском. */}
          <g
            onClick={spin}
            style={{ cursor: "pointer" }}
            role="button"
            aria-label="Крутити колесо"
          >
            <circle cx={C} cy={C} r={R_IN} fill="url(#whHub)" filter="url(#whHubS)" />
            <circle cx={C} cy={C} r={R_IN} fill="none" stroke="rgba(23,20,17,.10)" />
            <circle cx={C} cy={C} r={R_IN - 9} fill="none" stroke="rgba(254,107,4,.35)" strokeWidth={1.5} />
            <text
              x={C}
              y={C + 7}
              textAnchor="middle"
              fontFamily="Unbounded"
              fontSize={20}
              fontWeight={800}
              letterSpacing="-0.01em"
              fill="#FE6B04"
            >
              Крутити
            </text>
          </g>

          {/* Стрілка — чистий чорний трикутник над ободом. */}
          <g ref={pinRef} style={{ transformOrigin: `${C}px ${PIN_PIVOT_Y}px` }}>
            <path
              d={`M${C - 21},${PIN_PIVOT_Y} L${C + 21},${PIN_PIVOT_Y} L${C},${PIN_TIP_Y} Z`}
              fill="#171411"
              filter="url(#whHubS)"
            />
          </g>
        </svg>
      </div>

      {doubleRow && rowSpinId !== null && (
        <button
          type="button"
          onClick={() => setDoubleSpin(rowSpinId)}
          className="mb-3 flex w-full items-center gap-3 rounded-ios-card bg-surface p-4 text-left shadow-card-soft active:scale-[0.99] transition"
        >
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary/10 text-primary">
            <Icon name="rocket" size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-[0.875rem] font-bold leading-tight text-ink">
              Подвоїти приз
            </div>
            <div className="mt-1 truncate text-[0.6875rem] leading-relaxed text-ink-dim">
              {doubleRow.prize} → {doubleRow.doubled}
            </div>
          </div>
          <Icon name="arrow-right" size={16} className="shrink-0 text-ink-faint" />
        </button>
      )}

      {/* Платні оберти (owner 2026-09-23). Ліміту покупок немає (owner
          2026-09-24). Рядок «Які у мене шанси» під ним прибрано (owner
          2026-09-30). */}
      {packs.length > 0 && (
        <button
          type="button"
          onClick={() => setPacksOpen(true)}
          className="mb-6 flex w-full items-center gap-3 rounded-ios-card bg-surface p-4 text-left shadow-card-soft active:scale-[0.99] transition"
        >
          {/* Іконка з набору, не емодзі (owner 2026-09-23). Плитка й кегль —
              еталон рядка розділу з профілю: 40 px і рисунок 16. */}
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary/10 text-primary">
            <Icon name="ticket" size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-[0.875rem] font-bold leading-tight text-ink">
              Ще оберти
            </div>
            {/* Одним рядком (owner 2026-09-24): що можна виграти і за яку
                ціну — ціна одного оберту (найменший пакет). */}
            <div className="mt-1 truncate text-[0.6875rem] leading-relaxed text-ink-dim">
              Шанс виграти рік підписки за {oneSpinPrice} ₴
            </div>
          </div>
          <Icon name="arrow-right" size={16} className="shrink-0 text-ink-faint" />
        </button>
      )}

      {/* Топ перемог місяця (owner 2026-10-11; до того — «тижня»): 10
          найцінніших виграшів за останні 30 діб, від найкращого до
          найгіршого. Дані РЕАЛЬНІ, із сервера (owner 2026-09-05). Поки за
          місяць ніхто нічого не виграв, секції просто немає — як і раніше.
          Фото й імʼя ведуть у профіль людини в Telegram, але лише в тих, у
          кого є юзернейм: без нього профіль не відкрити, тому такий рядок —
          звичайний текст, без жодного натяку на натискання. Розмітка в
          обох випадках та сама, рядки не відрізняються ні на піксель. */}
      {topMonth.length > 0 && (
        <Section title="Топ перемог місяця" bodyClassName="p-0">
          {topMonth.map((w, i) => {
            const who = (
              <>
                <WinnerAvatar
                  key={w.avatar_url ?? "letter"}
                  name={w.name}
                  url={w.avatar_url}
                  tone={AVATAR_TONES[i % AVATAR_TONES.length]}
                />
                <div className="min-w-0">
                  <div className="truncate font-display text-[0.84375rem] font-semibold text-ink">
                    {w.name}
                  </div>
                  <div className="mt-0.5 text-[0.6875rem] text-ink-faint">
                    {agoLabel(w.at, renderedAt)}
                  </div>
                </div>
              </>
            );
            const whoClass = "flex min-w-0 items-center gap-3";
            return (
              <div
                key={`${w.at ?? ""}-${i}`}
                className={
                  "flex items-center gap-3 px-4 py-2.5" +
                  (i > 0 ? " border-t border-line" : "")
                }
              >
                <div className="flex min-w-0 flex-1">
                  {w.username ? (
                    <button
                      type="button"
                      onClick={() => openTelegramUserLink(`https://t.me/${w.username}`)}
                      aria-label={`Відкрити профіль ${w.name} в Telegram`}
                      className={whoClass + " text-left active:opacity-60"}
                    >
                      {who}
                    </button>
                  ) : (
                    <div className={whoClass}>{who}</div>
                  )}
                </div>
                <div
                  className={
                    "shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 font-display text-[0.6875rem] font-bold " +
                    (isBigWin(w.prize_key)
                      ? "bg-primary text-white"
                      : "bg-ink/[0.06] text-ink-dim")
                  }
                >
                  {WINNER_LABEL[w.prize_key] ?? "Приз"}
                </div>
              </div>
            );
          })}
        </Section>
      )}

      <WheelNoSpinPopup
        open={noSpinOpen}
        onClose={() => setNoSpinOpen(false)}
        nextAt={state.data?.next_grant_at}
        onBuy={
          packs.length > 0
            ? () => {
                setNoSpinOpen(false);
                setPacksOpen(true);
              }
            : undefined
        }
      />

      <WheelPacksPopup
        open={packsOpen}
        onClose={() => setPacksOpen(false)}
        packs={packs}
        pendingSlug={purchase.isPending ? (purchase.variables ?? null) : null}
        onBuy={buySpins}
      />

      <WheelPrizePopup
        // Поки питаємо «Точно не подвоювати?» — попап призу ховаємо, щоб вікна не лежали одне на одному.
        open={result !== null && prizeReady && !skipAsk}
        onClose={guard(closePrize)}
        emoji={result && isJackpot(result) ? "🎉" : (result?.emoji ?? "💎")}
        value={result ? prizeTitle(result) : ""}
        unit=""
        headline={result && isJackpot(result) ? "Джекпот!" : undefined}
        desc={result ? result.desc : ""}
        // «Перейти до стрічки» прибрано (owner 2026-10-26: «убрать кнопку к ленте»). Поки можна подвоїти — лише дві
        // кнопки: «Подвоїти» і «Крутити ще раз»; «Оформити підписку» / «Скористатися» повертаються, коли подвоєння немає.
        ctaLabel={
          canDouble
            ? undefined
            : result?.kind === "bonus"
              ? "Оформити підписку"
              : result?.kind === "promo"
                ? "Скористатися"
                : result?.kind === "spin"
                  ? "Крутити ще раз"
                  : undefined
        }
        onCta={guard(() => {
          const kind = result?.kind;
          closePrize();
          if (kind === "bonus") navigate("/subscription");
          else if (kind === "promo") navigate("/subscription");
          else spin();
        })}
        // «Спробувати ще» (owner 2026-09-23). Випав «ще один оберт» — кнопки
        // немає, головна вже крутить. Оберти ще лишились (куплений пакет) —
        // крутимо одразу. Інакше відкриваємо пакети (ліміту покупок немає).
        againLabel={
          !result || result.kind === "spin"
            ? undefined
            : state.data?.available
              ? "Крутити ще раз"
              : packs.length > 0
                ? "Спробувати ще"
                : undefined
        }
        onAgain={guard(() => {
          closePrize();
          if (state.data?.available) spin();
          else setPacksOpen(true);
        })}
        double={
          canDouble && dq.data
            ? {
                from: `${dq.data.amount ?? ""} ${dq.data.prize_key?.startsWith("bonus") ? "бонусів" : ukrDaysWord(dq.data.amount ?? 0)}`,
                to: `${dq.data.doubled_amount ?? ""} ${dq.data.prize_key?.startsWith("bonus") ? "бонусів" : ukrDaysWord(dq.data.doubled_amount ?? 0)}`,
                onClick: () => {
                  const id = lastSpinId;
                  closePrize();
                  setDoubleSpin(id);
                },
              }
            : undefined
        }
      />
      {skipAsk && dq.data && (
        <WheelDoubleSkipPopup
          prize={dq.data.prize ?? ""}
          doubled={dq.data.doubled ?? ""}
          busy={skipBusy}
          onDouble={() => {
            setSkipAsk(null);
            const id = lastSpinId;
            closePrize();
            setDoubleSpin(id);
          }}
          onSkip={async () => {
            setSkipBusy(true);
            if (lastSpinId) await markWheelDouble(lastSpinId, "declined").catch(() => undefined);
            setSkipBusy(false);
            const act = skipAsk;
            setSkipAsk(null);
            void qc.invalidateQueries({ queryKey: ["wheel", "double"] });
            act();
          }}
        />
      )}
      {doubleSpin !== null && (
        <WheelDoublePopup
          spinId={doubleSpin}
          onClose={() => {
            setDoubleSpin(null);
            void qc.invalidateQueries({ queryKey: ["wheel"] });
          }}
        />
      )}
    </div>
  );
}
